import type { Coordinates, SearchResult } from '@/types';

// ---------------------------------------------------------------------------
// Raw-coordinate parsing.
//
// Going to a lat/lng needs NO backend, so this is the reliable fallback while
// name/place geocoding is unavailable. `parseCoordinates` accepts the common
// ways people type a coordinate pair and returns a validated {lat,lng} or null.
//
// Supported formats (lat first, lng second unless hemispheres say otherwise):
//   "12.905, 77.595"       comma separated
//   "12.905 77.595"        space separated
//   "12.905,77.595"        comma, no space
//   "12.905°N, 77.595°E"   degree symbol + trailing hemisphere
//   "N12.905 E77.595"      leading hemisphere
//   "-12.905, +77.595"     explicit signs
// Hemisphere S/W => negative. lat is validated to [-90,90], lng to [-180,180];
// anything out of range is rejected (returns null).
// ---------------------------------------------------------------------------

type Axis = 'lat' | 'lng' | null;

interface Component {
  value: number;
  axis: Axis;
}

// One coordinate value: optional leading hemisphere, optional sign, a number,
// optional degree symbol, optional trailing hemisphere. e.g. "N12.905",
// "12.905°E", "-77.5". Rejects tokens with no number or two hemispheres.
const COMPONENT_RE = /^([NSEW])?\s*([+-]?\d+(?:\.\d+)?)\s*°?\s*([NSEW])?$/i;

function parseComponent(raw: string): Component | null {
  const m = raw.trim().match(COMPONENT_RE);
  if (!m) return null;

  const lead = m[1] ? m[1].toUpperCase() : '';
  const trail = m[3] ? m[3].toUpperCase() : '';
  if (lead && trail) return null; // two hemispheres on one value is ambiguous

  const hemi = lead || trail;
  let value = parseFloat(m[2]);
  if (Number.isNaN(value)) return null;

  let axis: Axis = null;
  if (hemi === 'N' || hemi === 'S') axis = 'lat';
  else if (hemi === 'E' || hemi === 'W') axis = 'lng';

  // S / W point in the negative direction.
  if (hemi === 'S' || hemi === 'W') value = -Math.abs(value);

  return { value, axis };
}

function inLatRange(v: number): boolean {
  return v >= -90 && v <= 90;
}

function inLngRange(v: number): boolean {
  return v >= -180 && v <= 180;
}

/**
 * Parse a free-text coordinate pair. Returns {lat,lng} when the input is a
 * valid, in-range coordinate, otherwise null (so callers can fall through to
 * name search). See the file header for supported formats.
 */
export function parseCoordinates(input: string): Coordinates | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;

  // Split into exactly two values: prefer comma, fall back to whitespace.
  const parts = raw.includes(',') ? raw.split(',') : raw.split(/\s+/);
  if (parts.length !== 2) return null;

  const a = parseComponent(parts[0]);
  const b = parseComponent(parts[1]);
  if (!a || !b) return null;

  let lat: number;
  let lng: number;
  let axesExplicit = false;

  if (a.axis === 'lat' && b.axis === 'lng') {
    lat = a.value;
    lng = b.value;
    axesExplicit = true;
  } else if (a.axis === 'lng' && b.axis === 'lat') {
    lat = b.value;
    lng = a.value;
    axesExplicit = true;
  } else if (a.axis === 'lat' && b.axis === null) {
    lat = a.value;
    lng = b.value;
    axesExplicit = true;
  } else if (a.axis === null && b.axis === 'lng') {
    lat = a.value;
    lng = b.value;
    axesExplicit = true;
  } else if (a.axis === 'lng' && b.axis === null) {
    lng = a.value;
    lat = b.value;
    axesExplicit = true;
  } else if (a.axis === null && b.axis === 'lat') {
    lat = b.value;
    lng = a.value;
    axesExplicit = true;
  } else if (a.axis === null && b.axis === null) {
    // No hemispheres: assume lat, lng order.
    lat = a.value;
    lng = b.value;
  } else {
    // Both values name the same axis (e.g. two latitudes) — ambiguous.
    return null;
  }

  // Only when the order was NOT pinned by hemispheres: if the first value can't
  // be a latitude but the pair works when swapped, swap it. Predictable and
  // limited — never overrides an explicit N/S/E/W.
  if (!axesExplicit && !inLatRange(lat) && inLatRange(lng) && inLngRange(lat)) {
    [lat, lng] = [lng, lat];
  }

  if (!inLatRange(lat) || !inLngRange(lng)) return null;

  return { lat, lng };
}

/** Human-readable "12.90503, 77.59528" label (trailing zeros trimmed). */
export function formatCoordinates(coords: Coordinates, precision = 5): string {
  const round = (n: number) => parseFloat(n.toFixed(precision)).toString();
  return `${round(coords.lat)}, ${round(coords.lng)}`;
}

/**
 * Build a synthetic SearchResult for a raw coordinate so it can flow through the
 * exact same select path as a real search hit (drops a marker, opens the place
 * card, enables "Directions"). The non-numeric `coord:` id makes PlaceDetails
 * skip its /places/{id} enrichment fetch and render straight from this object.
 */
export function coordinatesToSearchResult(coords: Coordinates): SearchResult {
  const label = formatCoordinates(coords);
  return {
    id: `coord:${coords.lat},${coords.lng}`,
    name: label,
    displayName: label,
    category: 'other',
    coordinates: coords,
    address: { formatted: label },
    relevanceScore: 1,
  };
}
